import { Box, Link, Separator, Spinner, VStack } from "@chakra-ui/react"
import Alert from "@pathwright/ui/src/components/alert/Alert"
import FullscreenConfirm from "@pathwright/ui/src/components/confirm/FullscreenConfirm"
import Select from "@pathwright/ui/src/components/form/form-select/ReactSelectInput"
import ToggleInput from "@pathwright/ui/src/components/form/form-toggle/ToggleInput"
import Tooltip from "@pathwright/ux/src/components/tooltip/Tooltip"
import { useState } from "react"
import {
  CreateContextualFeaturePermissionMutationVariables,
  FeatureAction,
  FeatureKey,
  FeaturePermission,
  useContextualFeaturePermissionQuery
} from "@pathwright/graphql-client/src/generated"
import { useIntercomContext } from "../../intercom/IntercomProvider"
import useUpdateCFP from "../useUpdateCFP"

const FeaturePermissionsHomeScreenForm = () => {
  const { isIntercomAvailable, showIntercomUIWithMessage } =
    useIntercomContext()

  const previewCFPQuery = useContextualFeaturePermissionQuery({
    variables: {
      featureKey: FeatureKey.HomeScreenPreview,
      featureAction: FeatureAction.View
    }
  })

  const viewCFPQuery = useContextualFeaturePermissionQuery({
    variables: {
      context: { user_id: null },
      featureKey: FeatureKey.HomeScreenUnified,
      featureAction: FeatureAction.HomeScreenView
    }
  })

  const previewCFP = previewCFPQuery.data?.contextualFeaturePermission
  const viewCFP = viewCFPQuery.data?.contextualFeaturePermission

  const [previewCFPMutation, previewCFPMutationState] = useUpdateCFP(
    previewCFP?.id
  )

  const defaultPreviewPermission = FeaturePermission.Disabled
  const currentPreviewPermission = previewCFP?.permission_in_context
  // Save preview option in state so it persists when navigating back
  // Initialize with Disabled as default, will be updated by useEffect
  const [selectedPreviewPermission, setSelectedPreviewPermission] =
    useState<FeaturePermission | null>(null)

  async function confirmPreviewChange(newPermission: FeaturePermission) {
    const variables: CreateContextualFeaturePermissionMutationVariables = {
      featureKey: FeatureKey.HomeScreenPreview,
      featureAction: FeatureAction.View,
      permissionInContext: newPermission,
      context: {
        user_id: null,
        cohort_id: null,
        resource_id: null
      }
    }

    const promise = previewCFPMutation(variables)

    if (!previewCFP?.id) {
      // If creating, wait for server response then fetch new data.
      await promise
      await previewCFPQuery.refetch()
    }
  }

  const loading = previewCFPQuery.loading || viewCFPQuery.loading
  const error =
    previewCFPQuery.error || viewCFPQuery.error || previewCFPMutationState.error

  if (loading) {
    return (
      <VStack py={4} justifyContent="center">
        <Spinner />
      </VStack>
    )
  }

  if (error) {
    return <Alert error={error} />
  }

  const handleLetUsKnow = () => {
    if (isIntercomAvailable) {
      showIntercomUIWithMessage(
        "Hello. I'm interested in launching the new Home Screen."
      )
    } else {
      window.open(
        "mailto:hello@pathwright.com?subject=Interest%20in%20launching%20the%20new%20Home%20Screen"
      )
    }
  }

  const handleNavigateToLaunch = () => {
    window.App?.navigate?.(window.location.pathname + "confirm-launch/")
  }

  const previewOptions = [
    { value: FeaturePermission.Disabled, label: "No one" },
    { value: FeaturePermission.AdminOnly, label: "Administrators" },
    { value: FeaturePermission.StaffOnly, label: "All Staff" },
    { value: FeaturePermission.Everyone, label: "Everyone" }
  ]

  const selectedOptionIndex = previewOptions.findIndex(
    (opt) =>
      opt.value ===
      (selectedPreviewPermission ||
        previewCFP?.permission_in_context ||
        defaultPreviewPermission)
  )
  const selectedPreviewOption = previewOptions[selectedOptionIndex]

  // A bit awkward, but we don't currenlty have a confirmation for disabling preview,
  // and maybe we don't need one anyhow, so only show the confirm dialog
  // when enabling preview for some permission context.
  const shouldConfirmPreviewChange =
    selectedPreviewPermission &&
    selectedPreviewPermission !== FeaturePermission.Disabled &&
    selectedPreviewPermission !== currentPreviewPermission

  const previewConfirm = shouldConfirmPreviewChange ? (
    <FullscreenConfirm
      icon="test-tube"
      heading="Enable Preview"
      body={
        <>
          Enabling the new Home Screen Preview displays a preview banner on the
          current Home Screen with a button to turn it on. (They can switch back
          to the old Home Screen anytime.){" "}
          {
            // TODO: home screen help article link
            false && (
              <Link
                href="https://help.pathwright.com/en/articles/510698-how-to-get-around"
                target="_blank"
                color="brand.400"
                textDecoration="underline"
              >
                Learn more about the preview.
              </Link>
            )
          }
        </>
      }
      confirmPrompt="Enable Preview"
      onConfirm={() => confirmPreviewChange(selectedPreviewPermission)}
      onCancel={() =>
        setSelectedPreviewPermission(currentPreviewPermission || null)
      }
      meta={
        // Max perm, omit "Only" (i.e. if Everyone).
        selectedOptionIndex === previewOptions.length - 1
          ? `For ${selectedPreviewOption?.label}`
          : `For ${selectedPreviewOption?.label} Only`
      }
    />
  ) : null

  return (
    <>
      {previewConfirm}
      <Box
        css={{
          "& h3": {
            fontWeight: 400,
            margin: 0,
            marginBottom: "8px"
          },
          "& p": {
            color: "rgb(161, 161, 161)",
            margin: 0,
            fontSize: "0.9em"
          }
        }}
      >
        <Box minW="full" py={4}>
          <h3>Enable preview</h3>
          <p>
            During the Labs phase, you can select which members have the option
            to try out the new Home Screen (they can switch back anytime).
          </p>
          <Select
            label="Enable preview for"
            labelWidth={200}
            value={selectedPreviewOption}
            options={previewOptions}
            onChange={(
              option: { value: FeaturePermission; label: string } | null
            ) => {
              if (!option) {
                return
              }

              setSelectedPreviewPermission(option.value)
              // Automatically confirm if disabling preview.
              if (option.value === FeaturePermission.Disabled) {
                confirmPreviewChange(option.value)
              }
            }}
          />
        </Box>
      </Box>
      <Separator borderBottomWidth={0} borderTopWidth="1px" w="full" />
      <Box
        css={{
          "& h3": {
            fontWeight: 400,
            margin: 0,
            marginBottom: "8px"
          },
          "& p": {
            color: "rgb(161, 161, 161)",
            margin: 0,
            fontSize: "0.9em"
          }
        }}
      >
        <Box
          minW="full"
          py={4}
          css={{
            "& .ToggleInput--disabled, & .ToggleInput--disabled *": {
              cursor: "not-allowed"
            }
          }}
        >
          <Tooltip
            title={
              <Box>
                Once the Lab Preview period is complete, you can enable the New
                Home Screen for all members to use.
              </Box>
            }
          >
            <ToggleInput
              labelWidth={300}
              label="Launch new Home Screen"
              value={
                viewCFP?.permission_in_context === FeaturePermission.Everyone
              }
              onChange={(value: boolean) => {
                if (value) {
                  // TODO: handle launch confirmation same as preview.
                  handleNavigateToLaunch()
                }
              }}
              // Once Home screen launch is made available,
              // if launched, this is disabled, since we don't want to allow reverting.
              // disabled={true}
              // For now, always disabled. We'll deploy a client change to allow launching later.
              disabled={true}
              alignRight
              style={{
                width: "100%"
              }}
            />
          </Tooltip>
          <p>
            Once the Lab Preview period is complete, you can enable the New Home
            Screen for all members to use.
          </p>
        </Box>
      </Box>
    </>
  )
}

export default FeaturePermissionsHomeScreenForm
